export const PLAN_TYPE = {
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  ANNUAL: 'annual',
}

export const TIER = {
  ESSENTIALS: 'essentials',
  PREMIUM: 'premium',
  AI_STUDIO: 'ai_studio',
}

export const SUBSCRIPTION_STATUS = {
  // Backend statuses
  ACTIVE: 'active',
  CANCELED: 'canceled',
  EXPIRED: 'expired',
  PAST_DUE: 'past_due',
  PAUSED: 'paused',
  REDEMPTION: 'redemption',
  SUSPENDED: 'suspended',
  REJECTED: 'rejected',
  // Derived UI statuses
  FULLY_CANCELED: 'fully_canceled',
  PENDING_CANCEL: 'pending_cancel',
  PAUSE_SCHEDULED: 'pause_scheduled',
}

/**
 * Maps a backend status + canceledAt + userPauseScheduledAt into a UI display status.
 * - canceled → FULLY_CANCELED
 * - active + canceledAt → PENDING_CANCEL (user initiated cancellation, still has access)
 * - active + userPauseScheduledAt → PAUSE_SCHEDULED (pause requested, starts at period end)
 * - everything else → pass-through (includes the backend `paused` status)
 */
export function resolveDisplayStatus(status, canceledAt, userPauseScheduledAt = null) {
  if (status === SUBSCRIPTION_STATUS.CANCELED) return SUBSCRIPTION_STATUS.FULLY_CANCELED
  // A live-pause cancel ends at once; the row stays paused until the cancel webhook lands.
  if (status === SUBSCRIPTION_STATUS.PAUSED && canceledAt) return SUBSCRIPTION_STATUS.FULLY_CANCELED
  if (status === SUBSCRIPTION_STATUS.ACTIVE && canceledAt) return SUBSCRIPTION_STATUS.PENDING_CANCEL
  if (status === SUBSCRIPTION_STATUS.ACTIVE && userPauseScheduledAt) return SUBSCRIPTION_STATUS.PAUSE_SCHEDULED
  return status
}

export const ALLOWANCE_STATE = {
  NONE: 'none',
  PAYMENT_FAILED: 'payment_failed',
}

export const PLAN_CHANGE_KIND = {
  UPGRADE: 'upgrade',
  DOWNGRADE: 'downgrade',
  TO_MONTHLY: 'to_monthly',
}

export const PLAN_CHANGE_TIMING = {
  IMMEDIATE: 'immediate',
  END_OF_PERIOD: 'end_of_period',
}

const RESTRUCTURE_STATUSES = [
  SUBSCRIPTION_STATUS.ACTIVE,
  SUBSCRIPTION_STATUS.PAUSE_SCHEDULED,
  SUBSCRIPTION_STATUS.PAUSED,
  SUBSCRIPTION_STATUS.PAST_DUE,
  SUBSCRIPTION_STATUS.REDEMPTION,
  SUBSCRIPTION_STATUS.PENDING_CANCEL,
  SUBSCRIPTION_STATUS.SUSPENDED,
  SUBSCRIPTION_STATUS.FULLY_CANCELED,
  SUBSCRIPTION_STATUS.EXPIRED,
]

export const isPaymentFailedStatus = (status) => (
  status === SUBSCRIPTION_STATUS.PAST_DUE || status === SUBSCRIPTION_STATUS.REDEMPTION
)

// Legacy scheduled switches and CGTrader AI keep the legacy layout until their own subtasks.
export function isRestructureLayout(subscription, plansRestructureEnabled) {
  if (!plansRestructureEnabled || !subscription || subscription.tier === TIER.AI_STUDIO) return false
  if (subscription.scheduledPlanSwitchTo) return false

  const displayStatus = resolveDisplayStatus(
    subscription.status, subscription.canceledAt, subscription.userPauseScheduledAt
  )
  return RESTRUCTURE_STATUSES.includes(displayStatus)
}

// Maps the `error` code returned by pause/resume endpoints to a translation key under
// subscription_management.pause.errors. Unknown codes fall back to `generic`.
export const PAUSE_ERROR_KEYS = [
  'subscription_not_found',
  'subscription_cannot_be_paused',
  'subscription_pause_already_scheduled',
  'subscription_billing_period_unavailable',
  'subscription_pause_start_must_be_future',
  'user_pause_allowance_exhausted',
  'subscription_cannot_be_resumed',
  'solidgate_request_failed',
]

// Codes that reuse another code's copy.
export const PAUSE_ERROR_ALIASES = {
  pause_not_available_on_annual: 'subscription_cannot_be_paused',
}
